import { useState, useEffect } from "react";
import { FiEye, FiSearch, FiCalendar, FiUser } from "react-icons/fi";
import { useDebouncedCallback } from "use-debounce";
import { getAllSmileCases } from "../../store/smileDesign/smileCasesSlice";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchFullCaseBySmileId } from "../../store/smileDesign/smileCasesSlice";
import SmileCaseDetailsModal from "../../components/smileDesign/SmileCaseDetailsModal";
import { fetchBoxAccessToken } from "../../store/common/authSlice";
import TablePagination from "../../components/smileDesign/TablePagination";
import { casesPortalCaseHref } from "../../utils/linkedPortalCase";

const AllSmiles = () => {
    const dispatch = useAppDispatch();
    const { user, boxAccessToken } = useAppSelector((state) => state.auth);
    const [showModal, setShowModal] = useState(false);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const {
        allSmileCases,
        allCasesPagination,
        isLoading,
        selectedCase,
        isFetchingCase,
    } = useAppSelector((state) => state.smileCases);

    const loadCases = (overrides?: { page?: number; search?: string }) => {
        dispatch(
            getAllSmileCases({
                page: overrides?.page ?? page,
                search: (overrides?.search ?? search) || undefined,
            })
        );
    };

    const debouncedSearch = useDebouncedCallback((value: string) => {
        setPage(1);
        loadCases({ page: 1, search: value });
    }, 400);

    useEffect(() => {
        loadCases({ page: 1 });
    }, []);

    const handleSearchChange = (value: string) => {
        setSearch(value);
        debouncedSearch(value);
    };

    const handleViewCase = async (smileId: string) => {
        await dispatch(fetchFullCaseBySmileId(smileId));
        setShowModal(true);
    };

    useEffect(() => {
        dispatch(fetchBoxAccessToken());
    }, [dispatch]);

    const handleSmileClick = (smileId: string) => {
        window.open(
            `/dashboard/smiledesign/generate/${smileId}`,
            "_blank",
            "noopener,noreferrer"
        );
    };

    return (
        <div className="min-h-screen bg-[#080808] p-3 sm:p-6 text-white rounded-2xl sm:rounded-3xl overflow-hidden">
            <div className="flex flex-col gap-2 mb-5 sm:mb-8 pl-12 md:pl-0">
                <h1 className="text-2xl sm:text-4xl font-bold bg-gradient-to-r from-[#2fa9bf] to-cyan-300 bg-clip-text text-transparent leading-tight">
                    All Smile Cases
                </h1>

                <p className="text-[#8b8f8d] text-sm sm:text-base">
                    Manage and review all smile design cases.
                </p>
            </div>

            <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-5 sm:mb-6">
                <div className="bg-[#101211] border border-[#1f2221] rounded-xl sm:rounded-2xl p-3 sm:p-5">
                    <p className="text-[#8b8f8d] text-sm truncate">Total Cases</p>
                    <h2 className="text-xl sm:text-3xl font-bold text-[#2fa9bf] mt-1 sm:mt-2">
                        {allCasesPagination.totalCount}
                    </h2>
                </div>

                <div className="bg-[#101211] border border-[#1f2221] rounded-xl sm:rounded-2xl p-3 sm:p-5">
                    <p className="text-[#8b8f8d] text-sm truncate">Current Page</p>
                    <h2 className="text-xl sm:text-3xl font-bold text-emerald-400 mt-1 sm:mt-2">
                        {allCasesPagination.currentPage}
                    </h2>
                </div>

                <div className="bg-[#101211] border border-[#1f2221] rounded-xl sm:rounded-2xl p-3 sm:p-5">
                    <p className="text-[#8b8f8d] text-sm truncate">Total Pages</p>
                    <h2 className="text-xl sm:text-3xl font-bold text-amber-400 mt-1 sm:mt-2">
                        {allCasesPagination.totalPages}
                    </h2>
                </div>
            </div>

            <div className="relative mb-4 sm:mb-6">
                <FiSearch className="absolute left-3.5 sm:left-4 top-1/2 -translate-y-1/2 text-[#6b6f6d] w-4 h-4" />
                <input
                    type="text"
                    placeholder="Search smile ID or patient..."
                    value={search}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="w-full bg-[#101211] border border-[#1f2221] rounded-xl sm:rounded-2xl py-2.5 sm:py-3 pl-10 sm:pl-12 pr-4 text-sm sm:text-base text-white placeholder:text-[#6b6f6d] focus:outline-none focus:ring-2 focus:ring-[#2fa9bf]/50"
                />
            </div>

            {/* Desktop table */}
            <div className="hidden lg:block bg-[#101211] border border-[#1f2221] rounded-3xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-[#141716] border-b border-[#1f2221]">
                                <th className="px-6 py-4 text-left text-sm font-semibold text-[#8b8f8d]">
                                    Smile ID
                                </th>
                                <th className="px-6 py-4 text-left text-sm font-semibold text-[#8b8f8d]">
                                    Patient Name
                                </th>
                                <th className="px-6 py-4 text-left text-sm font-semibold text-[#8b8f8d]">
                                    Cases portal
                                </th>
                                <th className="px-6 py-4 text-left text-sm font-semibold text-[#8b8f8d]">
                                    Created At
                                </th>
                                <th className="px-6 py-4 text-center text-sm font-semibold text-[#8b8f8d]">
                                    Actions
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td
                                        colSpan={5}
                                        className="text-center py-10 text-[#8b8f8d]"
                                    >
                                        Loading...
                                    </td>
                                </tr>
                            ) : allSmileCases.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={5}
                                        className="text-center py-10 text-[#8b8f8d]"
                                    >
                                        No smile cases found
                                    </td>
                                </tr>
                            ) : (
                                allSmileCases.map((smile) => (
                                    <tr
                                        key={smile.smile_id}
                                        className="border-b border-[#1f2221] hover:bg-[#141716] transition"
                                    >
                                        <td className="px-6 py-4">
                                            <button
                                                type="button"
                                                onClick={() => handleSmileClick(smile.smile_id)}
                                                className="font-medium text-[#2fa9bf] hover:text-[#36bfd8] hover:underline break-all text-left"
                                            >
                                                {smile.smile_id}
                                            </button>
                                        </td>

                                        <td className="px-6 py-4">
                                            {smile.patient_name}
                                        </td>

                                        <td className="px-6 py-4">
                                            {smile.case_id ? (
                                                <a
                                                    href={casesPortalCaseHref(smile.case_id, user?.role)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="font-medium text-[#2fa9bf] hover:text-[#36bfd8] hover:underline break-all"
                                                >
                                                    {smile.case_id}
                                                </a>
                                            ) : (
                                                <span className="text-[#8b8f8d]">—</span>
                                            )}
                                        </td>

                                        <td className="px-6 py-4">
                                            {new Date(
                                                smile.created_at
                                            ).toLocaleDateString()}
                                        </td>

                                        <td className="px-6 py-4">
                                            <div className="flex justify-center">
                                                <button
                                                    type="button"
                                                    onClick={() => handleViewCase(smile.smile_id)}
                                                    className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#2fa9bf]/10 border border-[#2fa9bf]/20 text-[#2fa9bf] hover:bg-[#2fa9bf] hover:text-white transition"
                                                >
                                                    <FiEye />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <TablePagination
                    pagination={allCasesPagination}
                    onPageChange={(nextPage) => {
                        setPage(nextPage);
                        loadCases({ page: nextPage });
                    }}
                    itemLabel="cases"
                />
            </div>

            {/* Mobile cards */}
            <div className="lg:hidden space-y-3">
                {isLoading ? (
                    <div className="text-center py-10 text-[#8b8f8d] bg-[#101211] border border-[#1f2221] rounded-2xl text-sm">
                        Loading...
                    </div>
                ) : allSmileCases.length === 0 ? (
                    <div className="text-center py-10 text-[#8b8f8d] bg-[#101211] border border-[#1f2221] rounded-2xl text-sm">
                        No smile cases found
                    </div>
                ) : (
                    allSmileCases.map((smile) => (
                        <div
                            key={smile.smile_id}
                            className="bg-[#101211] border border-[#1f2221] rounded-2xl p-3.5 active:scale-[0.995] transition"
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                    <button
                                        type="button"
                                        onClick={() => handleSmileClick(smile.smile_id)}
                                        className="font-medium text-[#2fa9bf] hover:text-[#36bfd8] break-all text-left text-sm leading-snug"
                                    >
                                        {smile.smile_id}
                                    </button>

                                    <div className="flex items-center gap-1.5 mt-2 text-white">
                                        <FiUser className="w-3.5 h-3.5 text-[#8b8f8d] shrink-0" />
                                        <p className="font-medium text-sm break-words">
                                            {smile.patient_name}
                                        </p>
                                    </div>

                                    <div className="flex items-center gap-1.5 mt-1.5 text-[#8b8f8d]">
                                        <FiCalendar className="w-3.5 h-3.5 shrink-0" />
                                        <p className="text-xs">
                                            {new Date(smile.created_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                    {smile.case_id ? (
                                        <a
                                            href={casesPortalCaseHref(smile.case_id, user?.role)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="mt-2 inline-block text-xs font-medium text-[#2fa9bf] hover:underline break-all"
                                        >
                                            Cases portal {smile.case_id}
                                        </a>
                                    ) : null}
                                </div>

                                <button
                                    type="button"
                                    onClick={() => handleViewCase(smile.smile_id)}
                                    className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#2fa9bf]/10 border border-[#2fa9bf]/20 text-[#2fa9bf] shrink-0 active:bg-[#2fa9bf] active:text-white transition"
                                    aria-label="View case"
                                >
                                    <FiEye />
                                </button>
                            </div>
                        </div>
                    ))
                )}
                <div className="bg-[#101211] border border-[#1f2221] rounded-2xl overflow-hidden">
                    <TablePagination
                        pagination={allCasesPagination}
                        onPageChange={(nextPage) => {
                            setPage(nextPage);
                            loadCases({ page: nextPage });
                        }}
                        itemLabel="cases"
                    />
                </div>
            </div>

            <SmileCaseDetailsModal
                show={showModal}
                onClose={() => setShowModal(false)}
                isFetchingCase={isFetchingCase}
                selectedCase={selectedCase}
                accessToken={boxAccessToken || ""}
            />
        </div>
    );
};

export default AllSmiles;
